/**
 * usePatientDetailsEditor — self-contained editor machinery for a patient's
 * Personal Info + Contact/Emergency groups, reusable outside the Patient
 * Workspace (e.g. the read-only/editable Patient Details drawer opened from
 * Open Patient for roles without Patient Workspace access).
 *
 * It mirrors the exact questionnaire/initial-answer/save logic that
 * `PatientShellLayout` runs inline for its Edit drawers. Kept as a standalone
 * hook (rather than refactoring the ~4.5k-line PatientShellLayout) to avoid
 * destabilizing the workspace; if the workspace logic changes, keep these two
 * in sync (search PatientShellLayout for `handleDetailsSave`/`handleContactsSave`).
 *
 * Everything is derived from `patientId` alone — no route params — so it can be
 * mounted anywhere. Pass '' to disable (queries return nothing).
 */
import { useCallback, useMemo } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../adapters/powersync';
import {
  qPatientById,
  qPatientContact,
  qPatientEmergencyContacts,
  qPatientFieldDefinitionOverrides,
  buildPatientFormSettingsVM,
  buildPatientValidationSchema,
  parseReportedAge,
  resolveReportedAgeWrite,
  type PatientDetailRow,
  type PatientContactRow,
  type PatientEmergencyContactRow,
  type PatientFieldDefinitionRow,
} from '@helios/data-access';
import { PATIENT_CORE_FIELDS, PATIENT_SEX_OPTIONS } from '@helios/domain';
import {
  buildStepQuestionnaire,
  type FieldOptionsMap,
} from '../pages/patient/buildStepQuestionnaire';
import type { IntakeAnswers, IntakeQuestionnaire, FieldRenderer } from '@helios/ui/intake';
import {
  ALL_COUNTRIES,
  CountryPicker,
  buildCountryOptions,
  getMobileCountryPickerConfig,
  dialCodeToIso2,
  iso2ToDialCode,
} from '@helios/ui/country-picker';
import { useEnabledIdTypeLabels } from '../hooks/useRegionalIdTypes';
import { usePatientAgeDobConflict, type PatientAgeConflict } from './patient-age-conflict';

function opt(value: string) {
  return { id: value, label: value, value };
}

const PERSONAL_INFO_OPTIONS: FieldOptionsMap = {
  doctor: ['Dr. Smith', 'Dr. Johnson', 'Dr. Williams'].map(opt),
  title: ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'].map(opt),
  idType: ['National ID', 'Passport', 'Driver License', 'SSN', 'Other'].map(opt),
  gender: [...PATIENT_SEX_OPTIONS].map(opt),
  maritalStatus: ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'].map(opt),
  referredBy: ['Walk-in', 'Doctor Referral', 'Online', 'Insurance'].map(opt),
  status: ['Active', 'Inactive', 'Archived'].map(opt),
};

const EMERGENCY_CONTACT_OPTIONS: FieldOptionsMap = {
  emergencyContactRelationship: [
    'Spouse',
    'Parent',
    'Child',
    'Sibling',
    'Relative',
    'Friend',
    'Other',
  ].map(opt),
};

export interface PatientDetailsEditor {
  patient: PatientDetailRow | undefined;
  contactRow: PatientContactRow | undefined;
  emergencyContact: PatientEmergencyContactRow | undefined;
  detailsQuestionnaire: IntakeQuestionnaire;
  detailsValidationSchema: string | null;
  initialDetailsAnswers: IntakeAnswers;
  handleDetailsSave: (answers: IntakeAnswers) => Promise<void>;
  /** PRD.315 — pass as the details drawer's `confirmBeforeSave`. */
  confirmDetailsSave: (answers: IntakeAnswers) => Promise<boolean>;
  /** PRD.315 — age/DOB reconciliation modal wiring. */
  ageConflict: PatientAgeConflict;
  contactsQuestionnaire: IntakeQuestionnaire;
  contactsValidationSchema: string | null;
  contactsFieldRenderers: Record<string, FieldRenderer>;
  initialContactsAnswers: IntakeAnswers;
  handleContactsSave: (answers: IntakeAnswers) => Promise<void>;
}

export function usePatientDetailsEditor(patientId: string): PatientDetailsEditor {
  const { db } = usePowerSyncDb();

  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId),
  );
  const { data: emergencyRows = [] } = usePowerSyncQuery<PatientEmergencyContactRow>(
    qPatientEmergencyContacts(patientId),
  );
  const { data: dbOverrides = [] } = usePowerSyncQuery<PatientFieldDefinitionRow>(
    qPatientFieldDefinitionOverrides(),
  );

  const patient = patientRows[0];
  const contactRow = contactRows[0];
  const emergencyContact = emergencyRows[0];

  const effectiveFields = useMemo(
    () => buildPatientFormSettingsVM(PATIENT_CORE_FIELDS, dbOverrides),
    [dbOverrides],
  );

  // ── Personal Info questionnaire ──
  const enabledIdTypeLabels = useEnabledIdTypeLabels();
  const personalInfoOptions = useMemo<FieldOptionsMap>(
    () => ({ ...PERSONAL_INFO_OPTIONS, idType: enabledIdTypeLabels.map(opt) }),
    [enabledIdTypeLabels],
  );
  const detailsQuestionnaire = useMemo(
    () => buildStepQuestionnaire(effectiveFields, 'Personal Info', personalInfoOptions),
    [effectiveFields, personalInfoOptions],
  );
  const detailsValidationSchema = useMemo(
    () => buildPatientValidationSchema(effectiveFields.filter((f) => f.step === 'Personal Info')),
    [effectiveFields],
  );

  // ── Contact + Emergency Contact questionnaire ──
  const contactsQuestionnaire = useMemo<IntakeQuestionnaire>(() => {
    const contactQ = buildStepQuestionnaire(effectiveFields, 'Contact');
    const ecQ = buildStepQuestionnaire(
      effectiveFields,
      'Emergency Contact',
      EMERGENCY_CONTACT_OPTIONS,
    );
    return {
      ...contactQ,
      id: 'patient-contacts-combined',
      sections: [...contactQ.sections, ...ecQ.sections],
    };
  }, [effectiveFields]);
  const contactsValidationSchema = useMemo(
    () =>
      buildPatientValidationSchema(
        effectiveFields.filter((f) => f.step === 'Contact' || f.step === 'Emergency Contact'),
      ),
    [effectiveFields],
  );

  const contactCountryConfig = useMemo(() => {
    const mccField = effectiveFields.find((f) => f.field_key === 'mobileCountryCode');
    return getMobileCountryPickerConfig(mccField?.validation_override);
  }, [effectiveFields]);
  const contactCountryOptions = useMemo(() => buildCountryOptions(ALL_COUNTRIES), []);

  const contactsFieldRenderers = useMemo<Record<string, FieldRenderer>>(
    () => ({
      mobileCountryCode: ({ value, onChange, readOnly }) => {
        const iso2 = typeof value === 'string' ? value : contactCountryConfig.defaultCountry;
        return (
          <CountryPicker
            options={contactCountryOptions}
            valueIso2={iso2}
            onChangeIso2={(newIso2) => onChange('mobileCountryCode', newIso2)}
            disabled={readOnly}
          />
        );
      },
    }),
    [contactCountryOptions, contactCountryConfig.defaultCountry],
  );

  // ── Initial answers ──
  const initialDetailsAnswers = useMemo<IntakeAnswers>(() => {
    const a: IntakeAnswers = {};
    if (patient) {
      if (patient.patient_number) a.patientId = patient.patient_number;
      if (patient.full_name) a.patientName = patient.full_name;
      if (patient.title) a.title = patient.title;
      if (patient.assigned_doctor) a.doctor = patient.assigned_doctor;
      if (patient.sex) a.gender = patient.sex;
      if (patient.birth_date) a.dateOfBirth = patient.birth_date;
      // PRD.315 — `!= null` rather than truthy: age 0 is a real reported value.
      if (patient.age_reported != null) a.age = String(patient.age_reported);
      if (patient.marital_status) a.maritalStatus = patient.marital_status;
      if (patient.local_name) a.localName = patient.local_name;
      if (patient.country_of_birth) a.countryOfBirth = patient.country_of_birth;
      if (patient.nationality) a.nationality = patient.nationality;
      if (patient.race) a.race = patient.race;
      if (patient.religion) a.religion = patient.religion;
      if (patient.occupation) a.occupation = patient.occupation;
    }
    return a;
  }, [patient]);

  const initialContactsAnswers = useMemo<IntakeAnswers>(() => {
    const a: IntakeAnswers = {};
    if (contactRow) {
      if (contactRow.email) a.email = contactRow.email;
      a.mobileCountryCode = dialCodeToIso2(
        contactRow.mobile_country_code,
        contactCountryOptions,
        contactCountryConfig.defaultCountry,
      );
      if (contactRow.mobile_number) a.mobileNumber = contactRow.mobile_number;
      if (contactRow.formal_telephone) a.telephone = contactRow.formal_telephone;
      if (contactRow.formal_postal_code) a.postalCode = contactRow.formal_postal_code;
      if (contactRow.formal_residential_address)
        a.residentialAddress = contactRow.formal_residential_address;
      if (contactRow.formal_province_state) a.provinceState = contactRow.formal_province_state;
      if (contactRow.formal_district) a.district = contactRow.formal_district;
    }
    if (emergencyContact) {
      if (emergencyContact.name) a.emergencyContactName = emergencyContact.name;
      if (emergencyContact.relationship)
        a.emergencyContactRelationship = emergencyContact.relationship;
      if (emergencyContact.phone) a.emergencyContactPhone = emergencyContact.phone;
      if (emergencyContact.email) a.emergencyContactEmail = emergencyContact.email;
    }
    return a;
  }, [contactRow, emergencyContact, contactCountryOptions, contactCountryConfig.defaultCountry]);

  // ── Save handlers (mirror PatientShellLayout) ──
  const handleDetailsSave = useCallback(
    async (answers: IntakeAnswers) => {
      if (!patientId || !db) return;
      const now = new Date().toISOString();
      const s = (k: string) =>
        typeof answers[k] === 'string' && answers[k] ? (answers[k] as string) : null;
      // PRD.315 — reported age + provenance. An invalid entry is treated as
      // "not reported" rather than coerced into a number; the IntakeRenderer's
      // number input and the validation schema keep it from getting this far.
      const age = resolveReportedAgeWrite({
        previousAge: patient?.age_reported,
        previousReportedAt: patient?.age_reported_at,
        nextAge: parseReportedAge(answers.age as string | undefined).value,
        now,
      });
      await db.execute(
        `UPDATE patients SET full_name = ?, title = ?, assigned_doctor = ?,
         sex = ?, birth_date = ?, age_reported = ?, age_reported_at = ?, marital_status = ?,
         local_name = ?, country_of_birth = ?, nationality = ?,
         race = ?, religion = ?, occupation = ?, updated_at = ?
         WHERE id = ?`,
        [
          s('patientName'),
          s('title'),
          s('doctor'),
          s('gender'),
          s('dateOfBirth'),
          age.age_reported,
          age.age_reported_at,
          s('maritalStatus'),
          s('localName'),
          s('countryOfBirth'),
          s('nationality'),
          s('race'),
          s('religion'),
          s('occupation'),
          now,
          patientId,
        ],
      );
    },
    [patientId, db, patient],
  );

  // PRD.315 — reconcile the entered DOB against the reported age AT ITS
  // REPORTING TIME before the write lands. Uses the same resolved pair the save
  // will persist, so an age edited in this session is checked against the new
  // reporting time and an untouched age against its original one.
  const ageConflict = usePatientAgeDobConflict();
  const guardAgeConflict = ageConflict.guard;
  const confirmDetailsSave = useCallback(
    (answers: IntakeAnswers) => {
      const dob =
        typeof answers.dateOfBirth === 'string' && answers.dateOfBirth ? answers.dateOfBirth : null;
      const age = resolveReportedAgeWrite({
        previousAge: patient?.age_reported,
        previousReportedAt: patient?.age_reported_at,
        nextAge: parseReportedAge(answers.age as string | undefined).value,
        now: new Date().toISOString(),
      });
      return guardAgeConflict({
        birthDate: dob,
        ageReported: age.age_reported,
        ageReportedAt: age.age_reported_at,
      });
    },
    [guardAgeConflict, patient],
  );

  const handleContactsSave = useCallback(
    async (answers: IntakeAnswers) => {
      if (!patientId || !db) return;
      const s = (k: string) =>
        typeof answers[k] === 'string' && answers[k] ? (answers[k] as string) : null;
      const mccIso2 = s('mobileCountryCode');
      const mccDialCode = mccIso2 ? iso2ToDialCode(mccIso2) : null;

      if (contactRow) {
        await db.execute(
          `UPDATE patient_contacts SET email = ?, mobile_country_code = ?, mobile_number = ?,
           formal_telephone = ?, formal_postal_code = ?,
           formal_residential_address = ?, formal_province_state = ?,
           formal_district = ?
           WHERE id = ?`,
          [
            s('email'),
            mccDialCode,
            s('mobileNumber'),
            s('telephone'),
            s('postalCode'),
            s('residentialAddress'),
            s('provinceState'),
            s('district'),
            contactRow.id,
          ],
        );
      } else {
        const contactId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO patient_contacts (id, patient_id, email, mobile_country_code, mobile_number,
           formal_telephone, formal_postal_code,
           formal_residential_address, formal_province_state, formal_district)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            contactId,
            patientId,
            s('email'),
            mccDialCode,
            s('mobileNumber'),
            s('telephone'),
            s('postalCode'),
            s('residentialAddress'),
            s('provinceState'),
            s('district'),
          ],
        );
      }

      const ecName = s('emergencyContactName');
      const ecPhone = s('emergencyContactPhone');
      if (emergencyContact) {
        await db.execute(
          `UPDATE patient_emergency_contacts SET name = ?, relationship = ?, phone = ?, email = ?
           WHERE id = ?`,
          [
            ecName,
            s('emergencyContactRelationship'),
            ecPhone,
            s('emergencyContactEmail'),
            emergencyContact.id,
          ],
        );
      } else if (ecName || ecPhone) {
        const ecId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO patient_emergency_contacts (id, patient_id, name, relationship, phone, email)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [
            ecId,
            patientId,
            ecName,
            s('emergencyContactRelationship'),
            ecPhone,
            s('emergencyContactEmail'),
          ],
        );
      }
    },
    [patientId, db, contactRow, emergencyContact],
  );

  return {
    patient,
    contactRow,
    emergencyContact,
    detailsQuestionnaire,
    detailsValidationSchema,
    initialDetailsAnswers,
    handleDetailsSave,
    confirmDetailsSave,
    ageConflict,
    contactsQuestionnaire,
    contactsValidationSchema,
    contactsFieldRenderers,
    initialContactsAnswers,
    handleContactsSave,
  };
}
